import { useState } from "react";
import { useFavorites } from "@/hooks/useLocalCollections";

interface Props {
  id: string;
  title: string;
}

export default function ActionShare({ id, title }: Props) {
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const favorite = isFavorite(id);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <div className="relative flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => {
          toggle(id);
          showToast(favorite ? "已取消收藏" : "已加入收藏（保存在本浏览器）");
        }}
        className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-xs transition-colors ${
          favorite
            ? "border-accent-500 bg-accent-100 text-accent-900"
            : "border-background-300 text-foreground-700 hover:border-accent-400"
        }`}
      >
        <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
        {favorite ? "已收藏" : "收藏"}
      </button>

      <button
        type="button"
        onClick={() => setPanelOpen((prev) => !prev)}
        aria-expanded={panelOpen}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs text-foreground-700 transition-colors hover:border-accent-400"
      >
        <i className="ri-share-forward-line" />
        分享
      </button>

      <button
        type="button"
        onClick={copyLink}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs text-foreground-700 transition-colors hover:border-accent-400"
      >
        <i className="ri-link" />
        复制链接
      </button>

      {panelOpen ? (
        <div className="absolute left-0 top-full z-20 mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-3 shadow-sm animate-fade-up">
          <p className="rule-label text-foreground-400">分享到</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { icon: "ri-weibo-fill", label: "微博" },
              { icon: "ri-wechat-fill", label: "微信" },
              { icon: "ri-link", label: "复制链接" },
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  setPanelOpen(false);
                  showToast(`已准备分享《${title}》到${item.label}（演示）`);
                }}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-background-200 px-2.5 py-2 text-[11px] text-foreground-700 transition-colors hover:border-accent-400"
              >
                <i className={`${item.icon} text-sm text-accent-700`} />
                {item.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setPanelOpen(false);
                showToast("已收藏，可稍后从收藏记录中查看");
                if (!favorite) toggle(id);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-background-200 px-2.5 py-2 text-[11px] text-foreground-700 transition-colors hover:border-accent-400"
            >
              <i className="ri-bookmark-line text-sm text-accent-700" />
              收藏
            </button>
          </div>
          <p className="mt-3 text-[10px] leading-relaxed text-foreground-400">
            分享为界面演示，不会真的发送内容。收藏数据仅保存在当前浏览器。
          </p>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-24 left-1/2 z-[80] -translate-x-1/2 rounded-full bg-primary-950 px-4 py-2 text-xs text-background-50 animate-fade-in md:bottom-28">
          {toast}
        </div>
      ) : null}
    </div>
  );
}